import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox,
  CircularProgress, MenuItem, Select, FormControl, InputLabel,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack,
} from '@mui/material';
import {
  DeleteForever as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
  Sync as SyncIcon,
  CleaningServices as CleanIcon,
} from '@mui/icons-material';
import { superadminAPI, gymsAPI, exerciseSyncAPI } from '../services/api';
import PageHeader from '../components/PageHeader';

// ── Stat tile ──────────────────────────────────────────────────────────────────
function StatTile({ label, value }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 2,
        transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Typography variant="h4" fontWeight={700} color="primary.main" sx={{ lineHeight: 1.1 }}>
        {value ?? '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {label}
      </Typography>
    </Paper>
  );
}

// ── Action card ────────────────────────────────────────────────────────────────
function ActionCard({ title, description, buttonLabel, buttonColor, icon, onClick }) {
  return (
    <Paper
      elevation={0}
      sx={{ p: 2.5, border: 1, borderColor: 'divider', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Typography variant="body1" fontWeight={600} gutterBottom color={`${buttonColor}.main`}>
        {icon} {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1, fontSize: '0.8125rem' }}>
        {description}
      </Typography>
      <Button variant="outlined" color={buttonColor} fullWidth onClick={onClick} startIcon={icon}>
        {buttonLabel}
      </Button>
    </Paper>
  );
}

export default function DatabaseManagement() {
  const [stats, setStats] = useState(null);
  const [gymUserCounts, setGymUserCounts] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const [resetDialog, setResetDialog] = useState(false);
  const [seedDialog, setSeedDialog] = useState(false);
  const [deleteUsersDialog, setDeleteUsersDialog] = useState(false);
  const [syncDialog, setSyncDialog] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [retentionDialog, setRetentionDialog] = useState(false);
  const [retentionResult, setRetentionResult] = useState(null);
  const [indianFoodDialog, setIndianFoodDialog] = useState(false);
  const [indianFoodResult, setIndianFoodResult] = useState(null);
  const [mealPlanDialog, setMealPlanDialog] = useState(false);
  const [mealPlanResult, setMealPlanResult] = useState(null);

  const [selectedCollections, setSelectedCollections] = useState([]);
  const [selectedSeedData, setSelectedSeedData] = useState([]);
  const [selectedGym, setSelectedGym] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, gymsRes] = await Promise.all([superadminAPI.getStats(), gymsAPI.getAll()]);
      const statsData = { ...statsRes.data.stats, mealLogRetention: statsRes.data.mealLogRetention };
      setStats(statsData);
      setGymUserCounts(statsRes.data.gymUserCounts);
      setRetentionResult(prev => prev ? { ...prev, preview: statsRes.data.mealLogRetention } : null);
      setGyms(gymsRes.data.data || gymsRes.data || []);
    } catch {
      setAlert({ severity: 'error', text: 'Failed to load database statistics' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetData = async () => {
    if (!selectedCollections.length) return;
    try {
      setActionLoading(true);
      const res = await superadminAPI.resetData(selectedCollections);
      setAlert({ severity: 'success', text: `Reset completed: ${JSON.stringify(res.data.results)}` });
      setResetDialog(false);
      setSelectedCollections([]);
      await loadData();
    } catch (err) {
      setAlert({ severity: 'error', text: err.response?.data?.message || 'Failed to reset data' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!selectedSeedData.length) return;
    try {
      setActionLoading(true);
      const res = await superadminAPI.seedData(selectedSeedData);
      setAlert({ severity: 'success', text: `Seeded: ${JSON.stringify(res.data.results)}` });
      setSeedDialog(false);
      setSelectedSeedData([]);
      await loadData();
    } catch (err) {
      setAlert({ severity: 'error', text: err.response?.data?.message || 'Failed to seed data' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUsersByGym = async () => {
    if (!selectedGym) return;
    try {
      setActionLoading(true);
      const res = await superadminAPI.deleteUsersByGym(selectedGym);
      setAlert({ severity: 'success', text: res.data.message });
      setDeleteUsersDialog(false);
      setSelectedGym('');
      await loadData();
    } catch (err) {
      setAlert({ severity: 'error', text: err.response?.data?.message || 'Failed to delete users' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunRetention = async () => {
    try {
      setActionLoading(true);
      const res = await superadminAPI.runMealRetention();
      setRetentionResult({ done: true, ...res.data });
      setAlert({ severity: 'success', text: res.data.message });
      await loadData();
    } catch (err) {
      setAlert({ severity: 'error', text: err.response?.data?.message || 'Retention cleanup failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleImportIndianFoods = async () => {
    try {
      setActionLoading(true);
      const res = await superadminAPI.importIndianFoods();
      setIndianFoodResult(res.data);
      setAlert({ severity: 'success', text: res.data.message });
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to import Indian food database';
      setAlert({ severity: 'error', text: msg });
      setIndianFoodResult({ success: false, message: msg, errors: err.response?.data?.errors || [] });
    } finally {
      setActionLoading(false);
    }
  };

  const handleImportMealPlans30 = async () => {
    try {
      setActionLoading(true);
      const res = await superadminAPI.importMealPlans30();
      setMealPlanResult(res.data);
      setAlert({ severity: 'success', text: res.data.message });
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to import 30-day meal plans';
      setAlert({ severity: 'error', text: msg });
      setMealPlanResult({ success: false, message: msg, error: err.response?.data?.error });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncExercises = async () => {
    try {
      setActionLoading(true);
      setSyncResult(null);
      const res = await exerciseSyncAPI.sync();
      setSyncResult(res.data);
      setAlert({ severity: 'success', text: res.data.message });
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Sync failed';
      setAlert({ severity: 'error', text: msg });
      setSyncResult({ success: false, message: msg });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleCollection = (val) =>
    setSelectedCollections(prev => prev.includes(val) ? prev.filter(c => c !== val) : [...prev, val]);

  const toggleSeed = (val) =>
    setSelectedSeedData(prev => prev.includes(val) ? prev.filter(d => d !== val) : [...prev, val]);

  const collectionOptions = [
    { value: 'users',           label: 'Users (keeps admins)' },
    { value: 'workoutPlans',    label: 'Workout Plans' },
    { value: 'mealPlans',       label: 'Meal Plans + Weekly Schedules' },
    { value: 'exercises',       label: 'Exercises' },
    { value: 'foodItems',       label: 'Food Items' },
    { value: 'goals',           label: 'Goals' },
    { value: 'workouts',        label: 'Workouts' },
    { value: 'logs',            label: 'All Logs (workout, meal, water, weight, session)' },
    { value: 'attendance',      label: 'Attendance Records' },
    { value: 'notifications',   label: 'Notifications' },
    { value: 'planEnrollments', label: 'Plan Enrollments' },
  ];

  const seedOptions = [
    { value: 'exercises',     label: 'Exercises (50+ gym, yoga, running, HIIT & more)' },
    { value: 'workoutPlans',  label: 'Starter Workout Plans (Beginner, Fat Loss, Overall Fitness - draft, trainer approval required)' },
    { value: 'mealPlans',     label: 'Starter Diet Plans (Balanced, Weight Loss, Muscle Gain - draft, trainer/nutrition approval required)' },
    { value: 'foodItems',     label: 'Food Database (50+ proteins, carbs, fats, dairy, legumes)' },
    { value: 'testUsers',     label: 'Test Users (user, trainer, gym admin accounts)' },
  ];

  const statTiles = [
    { label: 'Users',           key: 'users' },
    { label: 'Gyms',            key: 'gyms' },
    { label: 'Exercises',       key: 'exercises' },
    { label: 'Workout Plans',   key: 'workoutPlans' },
    { label: 'Meal Plans',      key: 'mealPlans' },
    { label: 'Food Items',      key: 'foodItems' },
    { label: 'Workout Logs',    key: 'workoutLogs' },
    { label: 'Meal Logs',       key: 'mealLogs' },
  ];

  return (
    <Box>

      {/* Header */}
      <PageHeader
        title="Database Management"
        subtitle="Superadmin tools for resetting and seeding data."
        gradient="linear-gradient(90deg, #DC2626, #F87171)"
        actions={<Button variant="outlined" size="small" startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />} onClick={loadData} disabled={loading}>Refresh</Button>}
      />


      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.text}
        </Alert>
      )}

      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600}>Superadmin only — destructive operations</Typography>
        <Typography variant="body2">These actions permanently delete data and cannot be undone.</Typography>
      </Alert>

      {/* Stats */}
      <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>Database Statistics</Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={1.5}>
            {statTiles.map(({ label, key }) => (
              <Grid item xs={6} sm={3} key={key}>
                <StatTile label={label} value={stats?.[key]} />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Gym user counts */}
      {gymUserCounts.length > 0 && (
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body1" fontWeight={600}>Users by Gym</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Gym</TableCell>
                  <TableCell align="right">Users</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gymUserCounts.map(gym => (
                  <TableRow key={gym.gymId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell><Typography variant="body2">{gym.gymName}</Typography></TableCell>
                    <TableCell align="right">
                      <Chip label={gym.userCount} size="small" color="primary" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Action cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ActionCard
            title="Reset Data"
            description="Permanently delete selected collections. Admin accounts are always preserved."
            buttonLabel="Reset Collections"
            buttonColor="error"
            icon={<DeleteIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
            onClick={() => setResetDialog(true)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ActionCard
            title="Seed Sample Data"
            description="Add sample exercises plus draft workout and diet plans for trainer review."
            buttonLabel="Seed Data"
            buttonColor="primary"
            icon={<AddIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
            onClick={() => setSeedDialog(true)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ActionCard
            title="Import Indian Food Database"
            description="Upsert Indian meals, dry fruits, nuts, and seeds with per-100g macros for meal logs."
            buttonLabel="Import Foods"
            buttonColor="success"
            icon={<RestaurantIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
            onClick={() => { setIndianFoodResult(null); setIndianFoodDialog(true); }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ActionCard
            title="Import 30-Day Meal Plans"
            description="Create guideline-informed balanced diet, weight loss, and weight gain plans with 30 strict meal days each."
            buttonLabel="Import Plans"
            buttonColor="success"
            icon={<RestaurantIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
            onClick={() => { setMealPlanResult(null); setMealPlanDialog(true); }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ActionCard
            title="Delete Gym Users"
            description="Remove all non-admin users from a specific gym."
            buttonLabel="Delete Users"
            buttonColor="warning"
            icon={<PeopleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
            onClick={() => setDeleteUsersDialog(true)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ActionCard
            title="Sync Exercises from Payload"
            description="Import exercises from scripts/exercises_payload.json into the database. Run seed_exercises.js first to generate the payload."
            buttonLabel="Sync Exercises"
            buttonColor="success"
            icon={<SyncIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
            onClick={() => { setSyncResult(null); setSyncDialog(true); }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ActionCard
            title="Meal Log Retention"
            description="Delete meal logs older than 30 days for free users. Premium users are never affected."
            buttonLabel="Run Cleanup"
            buttonColor="warning"
            icon={<CleanIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />}
            onClick={() => { setRetentionResult(null); setRetentionDialog(true); }}
          />
        </Grid>
      </Grid>

      {/* Reset Dialog */}
      <Dialog open={resetDialog} onClose={() => !actionLoading && setResetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon color="error" fontSize="small" />
            <Typography variant="h6">Reset Collections</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>This action is permanent and cannot be undone.</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select collections to delete:</Typography>
          <FormGroup>
            {collectionOptions.map(opt => (
              <FormControlLabel
                key={opt.value}
                control={<Checkbox size="small" checked={selectedCollections.includes(opt.value)} onChange={() => toggleCollection(opt.value)} />}
                label={<Typography variant="body2">{opt.label}</Typography>}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setResetDialog(false)} disabled={actionLoading}>Cancel</Button>
          <Button
            color="error" variant="contained" onClick={handleResetData}
            disabled={actionLoading || !selectedCollections.length}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {actionLoading ? 'Resetting…' : 'Reset Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seed Dialog */}
      <Dialog open={seedDialog} onClose={() => !actionLoading && setSeedDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddIcon color="primary" fontSize="small" />
            <Typography variant="h6">Seed Sample Data</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Starter workout and diet plans are seeded as drafts. A gym trainer or qualified nutrition coach should review, personalize, and publish them before assigning to members.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select data types to seed:</Typography>
          <FormGroup>
            {seedOptions.map(opt => (
              <FormControlLabel
                key={opt.value}
                control={<Checkbox size="small" checked={selectedSeedData.includes(opt.value)} onChange={() => toggleSeed(opt.value)} />}
                label={<Typography variant="body2">{opt.label}</Typography>}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSeedDialog(false)} disabled={actionLoading}>Cancel</Button>
          <Button
            color="primary" variant="contained" onClick={handleSeedData}
            disabled={actionLoading || !selectedSeedData.length}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            {actionLoading ? 'Seeding…' : 'Seed Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Indian Food Import Dialog */}
      <Dialog open={indianFoodDialog} onClose={() => !actionLoading && setIndianFoodDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RestaurantIcon color="success" fontSize="small" />
            <Typography variant="h6">Import Indian Food Database</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {!indianFoodResult ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This imports schema-safe FoodItem records for Indian meals, staples, legumes, dairy/protein dishes, dry fruits, nuts, and seeds.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every item includes category, dietary type, calories, protein, carbs, fat, fiber, serving size, and active status. Nutrition values are stored per 100g for meal-log calculations.
              </Typography>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Import behavior</Typography>
                <Typography variant="body2" color="text.secondary">
                  Existing foods with the same name are updated. Missing foods are created. No meal logs or plans are deleted.
                </Typography>
              </Paper>
            </>
          ) : (
            <>
              <Alert severity={indianFoodResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {indianFoodResult.message}
              </Alert>
              {indianFoodResult.success && (
                <Grid container spacing={1}>
                  {[
                    { label: 'Total Items', value: indianFoodResult.total },
                    { label: 'Inserted', value: indianFoodResult.inserted },
                    { label: 'Updated', value: indianFoodResult.updated },
                    { label: 'Matched', value: indianFoodResult.matched },
                  ].map(({ label, value }) => (
                    <Grid item xs={6} key={label}>
                      <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                        <Typography variant="h5" fontWeight={700} color="success.main">{value ?? 0}</Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
              {indianFoodResult.errors?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {indianFoodResult.errors.length} item(s) failed schema validation. Check server logs for details.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setIndianFoodDialog(false)} disabled={actionLoading}>
            {indianFoodResult ? 'Close' : 'Cancel'}
          </Button>
          {!indianFoodResult && (
            <Button
              color="success" variant="contained" onClick={handleImportIndianFoods}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <RestaurantIcon />}
            >
              {actionLoading ? 'Importing...' : 'Import Foods'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 30-Day Meal Plans Import Dialog */}
      <Dialog open={mealPlanDialog} onClose={() => !actionLoading && setMealPlanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RestaurantIcon color="success" fontSize="small" />
            <Typography variant="h6">Import 30-Day Meal Plans</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {!mealPlanResult ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                This creates three published strict plans: balanced diet, weight loss, and weight gain.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each plan has 30 days of prescribed meals informed by ICMR-NIN guidance, USDA MyPlate patterns, and the CDC plate method. The import also upserts required food items and creates weekly schedules for the mobile meal log.
              </Typography>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Import behavior</Typography>
                <Typography variant="body2" color="text.secondary">
                  Existing plans with the same names are updated. Meal logs and user enrollments are not deleted.
                </Typography>
              </Paper>
            </>
          ) : (
            <>
              <Alert severity={mealPlanResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {mealPlanResult.message}
              </Alert>
              {mealPlanResult.success && (
                <>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {[
                      { label: 'Plans', value: mealPlanResult.totalPlans },
                      { label: 'Plan Days', value: mealPlanResult.totalDays },
                      { label: 'Weekly Schedules', value: mealPlanResult.totalWeeklySchedules },
                    ].map(({ label, value }) => (
                      <Grid item xs={4} key={label}>
                        <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                          <Typography variant="h5" fontWeight={700} color="success.main">{value ?? 0}</Typography>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Stack spacing={1}>
                    {mealPlanResult.results?.map(plan => (
                      <Paper key={plan.name} elevation={0} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                        <Typography variant="body2" fontWeight={700}>{plan.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plan.days} days - {plan.dailyCaloriesTarget} kcal/day - {plan.dailyProteinTarget}g protein/day
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}
              {mealPlanResult.error && (
                <Alert severity="warning" sx={{ mt: 2 }}>{mealPlanResult.error}</Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMealPlanDialog(false)} disabled={actionLoading}>
            {mealPlanResult ? 'Close' : 'Cancel'}
          </Button>
          {!mealPlanResult && (
            <Button
              color="success" variant="contained" onClick={handleImportMealPlans30}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <RestaurantIcon />}
            >
              {actionLoading ? 'Importing...' : 'Import Plans'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Users by Gym Dialog */}
      <Dialog open={deleteUsersDialog} onClose={() => !actionLoading && setDeleteUsersDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon color="warning" fontSize="small" />
            <Typography variant="h6">Delete Users by Gym</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>All non-admin users from the selected gym will be deleted.</Alert>
          <FormControl fullWidth size="small">
            <InputLabel>Select Gym</InputLabel>
            <Select value={selectedGym} onChange={e => setSelectedGym(e.target.value)} label="Select Gym">
              {gyms.map(gym => (
                <MenuItem key={gym._id} value={gym._id}>
                  {gym.name} ({gymUserCounts.find(g => g.gymId === gym._id)?.userCount ?? 0} users)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteUsersDialog(false)} disabled={actionLoading}>Cancel</Button>
          <Button
            color="warning" variant="contained" onClick={handleDeleteUsersByGym}
            disabled={actionLoading || !selectedGym}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {actionLoading ? 'Deleting…' : 'Delete Users'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Meal Log Retention Dialog */}
      <Dialog open={retentionDialog} onClose={() => !actionLoading && setRetentionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CleanIcon color="warning" fontSize="small" />
            <Typography variant="h6">Meal Log Retention Cleanup</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {!retentionResult?.done ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will permanently delete meal logs older than <strong>30 days</strong> for all free users. Premium users are never affected.
              </Alert>
              {stats && (
                <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Preview (from last stats refresh)</Typography>
                  <Grid container spacing={1.5}>
                    {[
                      { label: 'Free users affected', value: stats.mealLogRetention?.freeUserCount ?? '—' },
                      { label: 'Logs to delete', value: stats.mealLogRetention?.logsEligibleForDeletion ?? '—' },
                      { label: 'Retention window', value: `${stats.mealLogRetention?.retentionDays ?? 30} days` },
                      { label: 'Cutoff date', value: stats.mealLogRetention?.cutoffDate ? new Date(stats.mealLogRetention.cutoffDate).toLocaleDateString() : '—' },
                    ].map(({ label, value }) => (
                      <Grid item xs={6} key={label}>
                        <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                          <Typography variant="h6" fontWeight={700} color="warning.main">{value}</Typography>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}
              <Typography variant="body2" color="text.secondary">
                Logs within the last 30 days are <strong>never deleted</strong>, regardless of user type.
              </Typography>
            </>
          ) : (
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600}>{retentionResult.message}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Cutoff: {retentionResult.cutoffDate ? new Date(retentionResult.cutoffDate).toLocaleString() : '—'}
                {' · '}Free users checked: {retentionResult.freeUserCount ?? '—'}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRetentionDialog(false)} disabled={actionLoading}>
            {retentionResult?.done ? 'Close' : 'Cancel'}
          </Button>
          {!retentionResult?.done && (
            <Button
              color="warning" variant="contained" onClick={handleRunRetention}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CleanIcon />}
            >
              {actionLoading ? 'Running…' : 'Run Cleanup'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Sync Exercises Dialog */}
      <Dialog open={syncDialog} onClose={() => !actionLoading && setSyncDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SyncIcon color="success" fontSize="small" />
            <Typography variant="h6">Sync Exercises from Payload</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {!syncResult ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Reads <strong>scripts/exercises_payload.json</strong>, copies videos to uploads, and upserts all exercises into the database.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                • New exercises → created with <strong>isPublished: true</strong><br />
                • Existing exercises → AI data + video URLs updated<br />
                • Manual edits to other fields are preserved
              </Typography>
            </>
          ) : (
            <>
              <Alert severity={syncResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {syncResult.message}
              </Alert>
              {syncResult.stats && (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {[
                    { label: 'Created',       value: syncResult.stats.created },
                    { label: 'Updated',       value: syncResult.stats.updated },
                    { label: 'Skipped',       value: syncResult.stats.skipped },
                    { label: 'Videos Copied', value: syncResult.stats.videosCopied },
                  ].map(({ label, value }) => (
                    <Grid item xs={6} key={label}>
                      <Paper elevation={0} sx={{ p: 1.5, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                        <Typography variant="h5" fontWeight={700} color="primary.main">{value ?? 0}</Typography>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
              {syncResult.errors?.length > 0 && (
                <Alert severity="warning">{syncResult.errors.length} exercise(s) failed — check server logs.</Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSyncDialog(false)} disabled={actionLoading}>
            {syncResult ? 'Close' : 'Cancel'}
          </Button>
          {!syncResult && (
            <Button
              color="success" variant="contained" onClick={handleSyncExercises}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            >
              {actionLoading ? 'Syncing…' : 'Start Sync'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
